import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  ColumnDef,
  ColumnFiltersState,
  HeaderGroup,
  Row,
  RowModel,
  Table,
  VisibilityState,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { URLS } from 'components/shared/App/constants';
import { selectors } from 'store';

import { FormattedJob, formatJobData } from './helpers';

export interface JobSearchTableHook {
  showTable: boolean;
  table: Table<FormattedJob>;
  header: HeaderGroup<FormattedJob>[];
  rows: RowModel<FormattedJob>;
  onClick: (row: any) => void;
}

export default function useJobSearchTable(): JobSearchTableHook {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    id: false,
  });
  const { push } = useHistory();

  const searchData = useSelector(selectors.infoSearch.getEntity);

  const onClick = useCallback(
    (row: Row<any>) => {
      const { original } = row;
      const id = original?.id;
      if (!id) return;
      push(URLS.job.replace(':id', id));
    },
    [push],
  );

  const data = useMemo(
    () => formatJobData(searchData?.hits?.job),
    [searchData],
  );

  const columns = useMemo<ColumnDef<FormattedJob, any>[]>(
    () => [
      {
        accessorKey: 'id',
        header: () => <p>Job ID</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'title',
        header: () => <p>Job Title</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerName',
        header: () => <p>Customer Name</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerEmail',
        header: () => <p>Customer Email</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerPhone',
        header: () => <p>Customer Phone</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerAddress',
        header: () => <p>Customer Address</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'createdAt',
        header: () => <p>Job Created</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'updatedAt',
        header: () => <p>Job Created</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
    ],
    [],
  );

  const table = useReactTable<FormattedJob>({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
      columnVisibility,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  return {
    showTable: data.length > 0,
    table,
    header: table.getHeaderGroups(),
    rows: table.getRowModel(),
    onClick,
  };
}
